import { ChevronDownIcon } from "@heroicons/react/24/outline"
import React, { useEffect, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"

import {
  DEFAULT_LOGO_FONT,
  getLogoFontsStylesheetUrl,
  LOGO_GOOGLE_FONTS,
  type LogoFontName
} from "../constants/logoFonts"

type FontSelectWithPreviewProps = {
  label: string
  value: string
  onChange: (font: LogoFontName) => void
  className?: string
  id?: string
}

export function FontSelectWithPreview({
  label,
  value,
  onChange,
  className,
  id
}: FontSelectWithPreviewProps): React.ReactElement {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const selectedFont = LOGO_GOOGLE_FONTS.includes(value as LogoFontName)
    ? (value as LogoFontName)
    : DEFAULT_LOGO_FONT

  // Load logo Google Fonts when this component is used
  useEffect(() => {
    const url = getLogoFontsStylesheetUrl()
    const existing = document.querySelector(`link[href="${url}"]`)
    if (existing) return
    const link = document.createElement("link")
    link.href = url
    link.rel = "stylesheet"
    link.crossOrigin = "anonymous"
    document.head.appendChild(link)
  }, [])

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener("click", handleClickOutside)
    }
    return () => document.removeEventListener("click", handleClickOutside)
  }, [open])

  return (
    <div ref={containerRef} className={twMerge("relative", className)}>
      {label && (
        <span className="mb-1 block text-sm font-bold text-gray-dark">{label}</span>
      )}
      <button
        id={id}
        type="button"
        onClick={() => setOpen(!open)}
        className={twMerge(
          "flex w-full items-center justify-between rounded-lg border border-gray px-3 py-2 text-left",
          "hover:border-black focus:border-black focus:outline-none focus:ring-4 focus:ring-blue"
        )}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`${label}: ${selectedFont}`}
      >
        <span
          className="truncate"
          style={{
            fontFamily: `'${selectedFont}', sans-serif`
          }}
        >
          {selectedFont}
        </span>
        <ChevronDownIcon
          className={twMerge("h-5 w-5 shrink-0 text-gray-dark transition-transform", open && "rotate-180")}
          aria-hidden
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 max-h-72 w-full overflow-auto rounded-lg border-2 border-gray bg-white py-2 shadow-lg"
          aria-label={label}
        >
          {LOGO_GOOGLE_FONTS.map((font) => (
            <li
              key={font}
              role="option"
              aria-selected={font === selectedFont}
              onClick={() => {
                onChange(font)
                setOpen(false)
              }}
              className={twMerge(
                "cursor-pointer px-3 py-2 transition-colors",
                font === selectedFont ? "bg-gray-ultralight" : "hover:bg-gray-ultralight"
              )}
              style={{
                fontFamily: `'${font}', sans-serif`
              }}
            >
              {font}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
